import {
    faBriefcase,
    faEnvelope,
    faFilter,
    faPhone,
    faSearch
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { sampleStaff, staffDesignations } from '../../data/data.ts';

const StaffListPage = () => {
  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDesignation, setSelectedDesignation] = useState('');
  const [loading, setLoading] = useState(true);



  useEffect(() => {
    // Simulate API call
    const loadStaff = async () => {
      setLoading(true);
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      setStaff(sampleStaff);
      setFilteredStaff(sampleStaff);
      setLoading(false);
    };

    loadStaff();
  }, []);

  useEffect(() => {
    let filtered = staff;

    // Filter by search term (name only)
    if (searchTerm) {
      filtered = filtered.filter(member =>
        member.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by designation
    if (selectedDesignation) {
      filtered = filtered.filter(member => member.designation === selectedDesignation);
    }

    setFilteredStaff(filtered);
  }, [searchTerm, selectedDesignation, staff]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-6 lg:mb-0">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Library Staff
              </h1>
              <p className="text-gray-600 text-lg">
                Meet our dedicated support staff at Jahangirnagar University Library
              </p>
              <div className="flex items-center space-x-4 mt-4">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  Total Staff: {filteredStaff.length}
                </span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  Active Designations: {staffDesignations.length}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search by Name */}
            <div className="relative">
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search by staff name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              />
            </div>

            {/* Designation Filter */}
            <div className="relative">
              <FontAwesomeIcon 
                icon={faFilter} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <select
                value={selectedDesignation}
                onChange={(e) => setSelectedDesignation(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 appearance-none bg-white"
              >
                <option value="">All Designations</option>
                {staffDesignations.map(designation => (
                  <option key={designation} value={designation}>{designation}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Active Filters */}
          {(searchTerm || selectedDesignation) && (
            <div className="mt-4 flex flex-wrap gap-2">
              {searchTerm && (
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                  Search: "{searchTerm}"
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {selectedDesignation && (
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">
                  Designation: {selectedDesignation}
                  <button 
                    onClick={() => setSelectedDesignation('')}
                    className="ml-2 text-purple-600 hover:text-purple-800"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Staff Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredStaff.map((member) => (
            <div 
              key={member.id}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
            >
              {/* Staff Photo */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-6 text-center">
                <div className="w-24 h-24 mx-auto bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                  {member.image ? (
                    <img src={member.image} alt={member.name} className="w-24 h-24 rounded-full object-cover" />
                  ) : (
                    <span>No Image</span>
                  )}
                </div>
                <h3 className="mt-4 text-xl font-bold text-gray-900">{member.name}</h3>
                <p className="text-green-600 font-semibold">{member.designation}</p>
              </div>

              {/* Staff Details */}
              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-3 text-gray-600">
                  <FontAwesomeIcon icon={faBriefcase} className="text-blue-500 w-4" />
                  <span className="text-sm">{member.department}</span>
                </div>

                <div className="flex items-center space-x-3 text-gray-600">
                  <FontAwesomeIcon icon={faEnvelope} className="text-green-500 w-4" />
                  <a 
                    href={`mailto:${member.email}`}
                    className="text-sm hover:text-blue-600 transition-colors duration-300"
                  >
                    {member.email}
                  </a>
                </div>

                <div className="flex items-center space-x-3 text-gray-600">
                  <FontAwesomeIcon icon={faPhone} className="text-red-500 w-4" />
                  <a 
                    href={`tel:${member.phone}`}
                    className="text-sm hover:text-blue-600 transition-colors duration-300"
                  >
                    {member.phone}
                  </a>
                </div>

                

                {/* Specialization Badge */}
                <div className="pt-4 border-t border-gray-100">
                  <span className="inline-block bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                    {member.specialization}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredStaff.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
            <FontAwesomeIcon icon={faSearch} className="text-gray-400 text-4xl mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Staff Found</h3>
            <p className="text-gray-600">
              Try adjusting your search criteria or filters to find what you're looking for.
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedDesignation('');
              }}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors duration-300"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffListPage;